// src/config/globalSettings.js

// ==========================================
// 1. GLOBAL SHOP SETTINGS
// ==========================================
export const ENABLE_RUSH_SHIPPING = false;
export const RUSH_PRICE = 35.00;
export const GLOBAL_DISCOUNT_PERCENT = 0.00;
export const SALE_NAME = "BLACK FRIDAY SALE";

// ==========================================
// 2. LEAD TIME SETTINGS
// ==========================================
export const QS_DAYS = 15;
export const RUSH_DAYS = 10;
export const CUSTOM_DAYS = 35;

export const QS_TIMELINE_TEXT = "9-15 Business Days";
export const RUSH_TIMELINE_TEXT = "10 Business Days (Expedited)";
export const CUSTOM_TIMELINE_TEXT = "4-7 Weeks";

// ------------------------------------------------------------------
// 2b. LEAD TIME LABELS + COLORS
// ------------------------------------------------------------------
// The phrase "QUICK SHIP" is deliberately gone from the customer-facing
// UI. The two programs are now distinguished by color only:
//   fast  -> brand green
//   custom -> brand gold
// Change the two labels below and every builder follows.
// Empty = the faster program shows no heading at all; the green
// "Lead Time: ..." line carries the message on its own.
export const QS_LABEL = "";
export const CUSTOM_LABEL = "CUSTOM BUILD";
export const RUSH_LABEL = "RUSH PRIORITY";

export const QS_COLOR = "#4caf50";      // brand green - faster program
export const CUSTOM_COLOR = "#D5A678";  // brand gold  - custom program
export const RUSH_COLOR = "#ff4444";

// 10% tint of a hex color, for the lead-time box background.
export const leadTimeTint = (hex) => `${hex}1A`;

// ==========================================
// 2c. ORDER TICKET LINES
// ==========================================
// Staff print the cart Configuration as the build ticket. A declined add-on
// used to print anyway ("Breakthrough Clean: No Thanks"), which buried the
// add-ons that WERE ordered. optionLine returns null for a declined value and
// every builder's configuration array already ends in .filter(Boolean), so the
// line simply disappears.
//
// Build SPECS are deliberately not covered here and still print, because the
// shop floor needs them: "Lightbearing: No", "Cant: No Cant (Standard)",
// "Optic Cut: No Optic Cut", "Retention: ...".
const DECLINED_VALUES = ["no thanks"];

// Cant option ids meaning "no cant" - the default build, so it is left off the
// order ticket and only an actual cant gets called out. Matched by ID, never by
// label: the two builders word it differently ("No Cant (Standard)" on IWB,
// "Straight Drop (0deg)" on OWB) and a future rename must not silently switch
// this off. Lightbearing is deliberately NOT treated this way - a non-light
// build still prints "Lightbearing: No", because that is a different mould and
// the floor should never have to infer it from an absent line.
export const NO_CANT_IDS = ["no_cant", "none"];

export const optionLine = (label, value) => {
  if (value === undefined || value === null) return null;
  const text = String(value).trim();
  if (!text || DECLINED_VALUES.includes(text.toLowerCase())) return null;
  return `${label}: ${text}`;
};

// ==========================================
// 3. CAMO / PATTERN MARKUP HELPER
// ==========================================
// We use this to dynamically overwrite camo pattern prices across builders
export const applyCamoMarkup = (colorsArray, flatPrice) => {
  return colorsArray.map(color => {
    // Safety check: Ensure the color actually has a name and type before checking
    const nameLower = color.name ? color.name.toLowerCase() : "";
    const typeLower = color.category ? color.category.toLowerCase() : "";

    // Category is authoritative. The name keywords below are only a fallback for
    // entries that have no category set — previously they ran unconditionally,
    // which caught the SOLID "Desert Tan" on the word 'desert' and billed it as
    // camo on every builder.
    const isCamo = typeLower
      ? (typeLower === 'camo' || typeLower === 'pattern')
      : (nameLower.includes('camo') ||
         nameLower.includes('multicam') ||
         nameLower.includes('woodland') ||
         nameLower.includes('tiger') ||
         nameLower.includes('topo') ||
         nameLower.includes('frog') ||
         nameLower.includes('m81') ||
         nameLower.includes('desert'));

    if (isCamo) {
        // OVERWRITES the price with the exact flat amount passed in
        return { ...color, price: flatPrice };
    }
    return color;
  });
};

// ==========================================
// 4. HELPER: COMMON COLOR DATA
// ==========================================
const definitions = {
    // --- SOLIDS ---
    black: { name: "Black", hex: "#1a1a1a", category: "solid", img: "/anr-builder/images/swatches/black.png" },
    arctic_white: { name: "Arctic White", hex: "#f0f0f0", category: "solid" },
    od_green: { name: "OD Green", hex: "#4b5320", category: "solid", img: "/anr-builder/images/swatches/od_green.png" },
    coyote: { name: "Coyote Tan", hex: "#81613e", category: "solid", img: "/anr-builder/images/swatches/coyote.png" },
    dark_grey: { name: "Dark Grey", hex: "#707276", category: "solid" }, 
    desert_tan: { name: "Desert Tan", hex: "#D2B48C", category: "solid" },
    fde: { name: "Flat Dark Earth (FDE)", hex: "#6e5c46", category: "solid" },
    blood_red: { name: "Blood Red", hex: "#660000", category: "solid" },
    tiffany_blue: { name: "Tiffany Blue", hex: "#0ABAB5", category: "solid" },
    pink: { name: "Pink", hex: "#FFC0CB", category: "solid" },
    purple: { name: "Purple", hex: "#800080", category: "solid", img: "/anr-builder/images/swatches/purple.jpg" },
    foliage_green: { name: "Foliage Green", hex: "#5d6658", category: "solid" },
    royal_blue: { name: "Royal Blue", hex: "#4169E1", category: "solid" },
    mas_grey: { name: "Mas Grey", hex: "#555b5f", category: "solid", img: "/anr-builder/images/swatches/mas_gray.jpg" },
    light_grey: { name: "Light Grey", hex: "#d3d3d3", category: "solid" },
    storm_grey: { name: "Storm Grey", hex: "#444444", category: "solid" },
    army_green: { name: "Army Green", hex: "#454b1b", category: "solid" },
    coyote_grey: { name: "Coyote Grey", hex: "#8b8d7a", category: "solid" },

    // --- CARBONS ---
    carbon_black: { name: "Carbon Fiber 2.0", hex: "#111", category: "carbon", img: "/anr-builder/images/swatches/carbon_fiber.jpg" },
    carbon_red: { name: "Carbon Fiber Blood Red 2.0", hex: "#500", category: "carbon", img: "/anr-builder/images/swatches/carbon_red.jpg" },
    carbon_fde: { name: "Carbon Fiber FDE 2.0", hex: "#4b3621", category: "carbon", img: "/anr-builder/images/swatches/carbon_fde.jpg" },
    carbon_foliage: { name: "Carbon Foliage Green", hex: "#4a5446", category: "carbon", img: "/anr-builder/images/swatches/carbon_foliage.jpg" },
    carbon_coyote: { name: "Carbon Coyote Tan", hex: "#5c4033", category: "carbon", img: "/anr-builder/images/swatches/carbon_coyote.jpg" },
    carbon_blue: { name: "Carbon Fiber Blue 2.0", hex: "#000055", category: "carbon", img: "/anr-builder/images/swatches/carbon_blue.jpg" },
    carbon_od: { name: "Carbon Fiber OD", hex: "#3b421a", category: "carbon", img: "/anr-builder/images/swatches/carbon_od.jpg" },
    // ADDED TO FIX CRASH:
    carbon_od_boltaron: { name: "Carbon Fiber OD (Boltaron)", hex: "#3b421a", category: "carbon", img: "/anr-builder/images/swatches/carbon_od.jpg" },

    // --- TEXTURES ---
    tru_whip: { name: "Tru-Hide Whip", hex: "#2a2a2a", category: "texture", img: "/anr-builder/images/swatches/truhide_whip.jpg" },
    tru_whiskey: { name: "Tru-Hide Whiskey", hex: "#8d5524", category: "texture", img: "/anr-builder/images/swatches/truhide_whiskey.jpg" },

    // --- CAMO ---
    multicam_alpine: { name: "Crye Multicam Alpine", category: "camo", hex: "#eee", img: "/anr-builder/images/swatches/multicam_alpine.jpg" },
    multicam_arid: { name: "Crye Multicam Arid", category: "camo", hex: "#a69e80", img: "/anr-builder/images/swatches/multicam_arid.jpg" },
    multicam: { name: "Crye Multicam", category: "camo", hex: "#5b664d", img: "/anr-builder/images/swatches/multicam.jpg" },
    multicam_black: { name: "Crye Multicam Black", category: "camo", hex: "#2e3234", img: "/anr-builder/images/swatches/multicam_black.jpg" },
    
    multicam_tropic: { name: "Crye Multicam Tropic", category: "camo", hex: "#2a3b2a", img: "/anr-builder/images/swatches/multicam_tropic.jpg" },
    hexcam_spectre: { name: "Hexcam Spectre13", category: "camo", img: "/anr-builder/images/swatches/hexcam_spectre13.jpg" },
    hexcam_wasteland: { name: "Hexcam Wasteland", category: "camo", img: "/anr-builder/images/swatches/hexcam_wasteland.jpg" },
    kryptek_coyote: { name: "Kryptek Coyote Tan", category: "camo", img: "/anr-builder/images/swatches/kryptek_coyote.jpg" },
    kryptek_mandrake: { name: "Kryptek Altitude", category: "camo", img: "/anr-builder/images/swatches/kryptek_mandrake.jpg" },
    kryptek_highlander: { name: "Kryptek Highlander", category: "camo", img: "/anr-builder/images/swatches/kryptek_highlander.jpg" },
    kryptek_nomad: { name: "Kryptek Nomad", category: "camo", img: "/anr-builder/images/swatches/kryptek_nomad.jpg" },
    kryptek_neptune: { name: "Kryptek Neptune", category: "camo", img: "/anr-builder/images/swatches/kryptek_neptune.jpg" },
    kryptek_od: { name: "Kryptek OD Green", category: "camo", img: "/anr-builder/images/swatches/kryptek_od.jpg" },
    kryptek_typhon: { name: "Kryptek Typhon", category: "camo", img: "/anr-builder/images/swatches/kryptek_typhon.jpg" },
    atacs_au: { name: "ATACs AU", category: "camo", img: "/anr-builder/images/swatches/atacs_au.jpg" },
    atacs_fg: { name: "ATACs FG", category: "camo", img: "/anr-builder/images/swatches/atacs_fg.jpg" },
    atacs_le: { name: "ATACs LE", category: "camo", img: "/anr-builder/images/swatches/atacs_le.jpg" },
    recon_haze: { name: "Recon Haze", category: "camo", img: "/anr-builder/images/swatches/recon_haze.jpg" },
    recon_monsoon: { name: "Recon Monsoon", category: "camo", img: "/anr-builder/images/swatches/recon_monsoon.jpg" },
    recon_fog: { name: "Recon Fog", category: "camo", img: "/anr-builder/images/swatches/recon_fog.jpg" },
    recon_dust: { name: "Recon Dust", category: "camo", img: "/anr-builder/images/swatches/recon_dust.jpg" },
    og_woodland: { name: "OG Woodland", category: "camo", img: "/anr-builder/images/swatches/og_woodland.jpg" },
    woodland_gen2: { name: "Woodland Gen 2", category: "camo", img: "/anr-builder/images/swatches/woodland_gen2.jpg" },
    woodland_peanut: { name: "Woodland Peanut Butter", category: "camo", img: "/anr-builder/images/swatches/woodland_peanutbutter.jpg" },
    woodland_notte: { name: "Woodland Notte", category: "camo", img: "/anr-builder/images/swatches/woodland_notte.jpg" },
    relv_moab: { name: "RELV Moab", category: "camo", img: "/anr-builder/images/swatches/relv_moab.jpg" },
    
    // --- NEW CAMOS ---
    relv_copperhead: { name: "RELV Copperhead", category: "camo", hex: "#6b4a2f", img: "/anr-builder/images/swatches/relv_copperhead.jpg" },
    kryptek_obskura_loki: { name: "Kryptek Obskura Loki", category: "camo", img: "/anr-builder/images/swatches/kryptek_obskura_loki.jpg" },
    relv_dynohyde: { name: "RELV Dynohyde", category: "camo", img: "/anr-builder/images/swatches/relv_dinohyde.jpg" },
};

// ==========================================
// 5. COLOR LIST FOR HOLSTERS (Big Items)
// ==========================================
export const GLOBAL_COLORS_HOLSTER = [
    { id: "black", ...definitions.black, price: 0 },

    // Solids $5
    { id: "arctic_white", ...definitions.arctic_white, price: 5 },
    { id: "od_green", ...definitions.od_green, price: 5 },
    { id: "coyote", ...definitions.coyote, price: 5 },
    { id: "dark_grey", ...definitions.dark_grey, price: 5 }, 
    { id: "desert_tan", ...definitions.desert_tan, price: 5 },
    { id: "fde", ...definitions.fde, price: 5 },
    { id: "blood_red", ...definitions.blood_red, price: 5 },
    { id: "tiffany_blue", ...definitions.tiffany_blue, price: 5 },
    { id: "pink", ...definitions.pink, price: 5 },
    { id: "purple", ...definitions.purple, price: 5 },
    { id: "foliage_green", ...definitions.foliage_green, price: 5 },
    { id: "royal_blue", ...definitions.royal_blue, price: 5 },
    { id: "mas_grey", ...definitions.mas_grey, price: 5 },
    { id: "light_grey", ...definitions.light_grey, price: 5 },
    { id: "storm_grey", ...definitions.storm_grey, price: 5 },
    { id: "army_green", ...definitions.army_green, price: 5 },
    { id: "coyote_grey", ...definitions.coyote_grey, price: 5 },

    // Carbon $7 (Disabled Foliage, Coyote, and OD)
    { id: "carbon_black", ...definitions.carbon_black, price: 7 },
    { id: "carbon_red", ...definitions.carbon_red, price: 7 },
    { id: "carbon_fde", ...definitions.carbon_fde, price: 7 },
    // { id: "carbon_foliage", ...definitions.carbon_foliage, price: 7 },
    // { id: "carbon_coyote", ...definitions.carbon_coyote, price: 7 },
    { id: "carbon_blue", ...definitions.carbon_blue, price: 7 },
    // { id: "carbon_od", ...definitions.carbon_od, price: 7 }, 

    // Texture $7
    { id: "tru_whip", ...definitions.tru_whip, price: 7 },
    { id: "tru_whiskey", ...definitions.tru_whiskey, price: 7 },

    // Camo $10
    { id: "multicam_alpine", ...definitions.multicam_alpine, price: 10 },
    { id: "multicam_arid", ...definitions.multicam_arid, price: 10 },
    { id: "multicam", ...definitions.multicam, price: 10 },
    { id: "multicam_black", ...definitions.multicam_black, price: 10 },
    { id: "multicam_tropic", ...definitions.multicam_tropic, price: 10 },
    { id: "hexcam_spectre", ...definitions.hexcam_spectre, price: 10 },
    { id: "hexcam_wasteland", ...definitions.hexcam_wasteland, price: 10 },
    { id: "kryptek_coyote", ...definitions.kryptek_coyote, price: 10 },
    { id: "kryptek_mandrake", ...definitions.kryptek_mandrake, price: 10 },
    { id: "kryptek_highlander", ...definitions.kryptek_highlander, price: 10 },
    { id: "kryptek_nomad", ...definitions.kryptek_nomad, price: 10 },
    { id: "kryptek_neptune", ...definitions.kryptek_neptune, price: 10 },
    { id: "kryptek_od", ...definitions.kryptek_od, price: 10 },
    { id: "kryptek_typhon", ...definitions.kryptek_typhon, price: 10 },
    { id: "atacs_au", ...definitions.atacs_au, price: 10 },
    { id: "atacs_fg", ...definitions.atacs_fg, price: 10 },
    { id: "atacs_le", ...definitions.atacs_le, price: 10 },
    { id: "recon_haze", ...definitions.recon_haze, price: 10 },
    { id: "recon_monsoon", ...definitions.recon_monsoon, price: 10 },
    { id: "recon_fog", ...definitions.recon_fog, price: 10 },
    { id: "recon_dust", ...definitions.recon_dust, price: 10 },
    { id: "og_woodland", ...definitions.og_woodland, price: 10 },
    { id: "woodland_gen2", ...definitions.woodland_gen2, price: 10 },
    { id: "woodland_peanut", ...definitions.woodland_peanut, price: 10 },
    { id: "woodland_notte", ...definitions.woodland_notte, price: 10 },
    { id: "relv_moab", ...definitions.relv_moab, price: 10 },
    
    // Premium $20
    { id: "relv_copperhead", ...definitions.relv_copperhead, price: 20 },
    { id: "kryptek_obskura_loki", ...definitions.kryptek_obskura_loki, price: 20 },
    { id: "relv_dynohyde", ...definitions.relv_dynohyde, price: 20 },
];

// ==========================================
// 6. COLOR LIST FOR MAGS / SMALL PARTS (Cheaper)
// ==========================================
export const GLOBAL_COLORS_MAG = [
    { id: "black", ...definitions.black, price: 0 },
    { id: "arctic_white", ...definitions.arctic_white, price: 0 },
    { id: "od_green", ...definitions.od_green, price: 0 },
    { id: "coyote", ...definitions.coyote, price: 0 },
    { id: "dark_grey", ...definitions.dark_grey, price: 0 }, 
    { id: "desert_tan", ...definitions.desert_tan, price: 0 },
    { id: "fde", ...definitions.fde, price: 0 },
    { id: "blood_red", ...definitions.blood_red, price: 0 },
    { id: "tiffany_blue", ...definitions.tiffany_blue, price: 0 },
    { id: "pink", ...definitions.pink, price: 0 },
    { id: "purple", ...definitions.purple, price: 0 },
    { id: "foliage_green", ...definitions.foliage_green, price: 0 },
    { id: "royal_blue", ...definitions.royal_blue, price: 0 },
    { id: "mas_grey", ...definitions.mas_grey, price: 0 },
    { id: "light_grey", ...definitions.light_grey, price: 0 },
    { id: "storm_grey", ...definitions.storm_grey, price: 0 },
    { id: "army_green", ...definitions.army_green, price: 0 },
    { id: "coyote_grey", ...definitions.coyote_grey, price: 0 },
    
    // Carbon $4 (Disabled Foliage, Coyote, and OD)
    { id: "carbon_black", ...definitions.carbon_black, price: 4 },
    { id: "carbon_red", ...definitions.carbon_red, price: 4 },
    { id: "carbon_fde", ...definitions.carbon_fde, price: 4 },
    // { id: "carbon_foliage", ...definitions.carbon_foliage, price: 4 },
    // { id: "carbon_coyote", ...definitions.carbon_coyote, price: 4 },
    { id: "carbon_blue", ...definitions.carbon_blue, price: 4 },
    // { id: "carbon_od", ...definitions.carbon_od, price: 4 },
    
    { id: "tru_whip", ...definitions.tru_whip, price: 4 },
    { id: "tru_whiskey", ...definitions.tru_whiskey, price: 4 },
    { id: "multicam_alpine", ...definitions.multicam_alpine, price: 10 },
    { id: "multicam_arid", ...definitions.multicam_arid, price: 10 },
    { id: "multicam", ...definitions.multicam, price: 10 },
    { id: "multicam_black", ...definitions.multicam_black, price: 10 },
    { id: "multicam_tropic", ...definitions.multicam_tropic, price: 10 },
    { id: "hexcam_spectre", ...definitions.hexcam_spectre, price: 10 },
    { id: "hexcam_wasteland", ...definitions.hexcam_wasteland, price: 10 },
    { id: "kryptek_coyote", ...definitions.kryptek_coyote, price: 10 },
    { id: "kryptek_highlander", ...definitions.kryptek_highlander, price: 10 },
    { id: "kryptek_nomad", ...definitions.kryptek_nomad, price: 10 },
    { id: "kryptek_mandrake", ...definitions.kryptek_mandrake, price: 10 }, 
    { id: "kryptek_neptune", ...definitions.kryptek_neptune, price: 10 },
    { id: "kryptek_od", ...definitions.kryptek_od, price: 10 },
    { id: "kryptek_typhon", ...definitions.kryptek_typhon, price: 10 },
    { id: "atacs_au", ...definitions.atacs_au, price: 10 },
    { id: "atacs_fg", ...definitions.atacs_fg, price: 10 },
    { id: "atacs_le", ...definitions.atacs_le, price: 10 },
    { id: "recon_haze", ...definitions.recon_haze, price: 10 },
    { id: "recon_monsoon", ...definitions.recon_monsoon, price: 10 },
    { id: "recon_fog", ...definitions.recon_fog, price: 10 },
    { id: "recon_dust", ...definitions.recon_dust, price: 10 },
    { id: "og_woodland", ...definitions.og_woodland, price: 10 },
    { id: "woodland_gen2", ...definitions.woodland_gen2, price: 10 },
    { id: "woodland_peanut", ...definitions.woodland_peanut, price: 10 },
    { id: "woodland_notte", ...definitions.woodland_notte, price: 10 },
    { id: "relv_moab", ...definitions.relv_moab, price: 10 },
    
    // Premium $20
    { id: "relv_copperhead", ...definitions.relv_copperhead, price: 20 },
    { id: "kryptek_obskura_loki", ...definitions.kryptek_obskura_loki, price: 20 },
    { id: "relv_dynohyde", ...definitions.relv_dynohyde, price: 20 },
];

// ==========================================
// 7. COLOR LIST FOR NIDHOGG LEVEL 2 (Limited)
// ==========================================
export const GLOBAL_COLORS_NIDHOGG_L2 = [
    // Standard (Price: $0 - $10)
    { id: "black", ...definitions.black, price: 0 },
    { id: "coyote", ...definitions.coyote, price: 10 },
    { id: "od_green", ...definitions.od_green, price: 10 },
    { id: "light_grey", ...definitions.light_grey, price: 10 },
    { id: "foliage_green", ...definitions.foliage_green, price: 10 },

    // Carbon (Price: $25)
    { id: "carbon_black", ...definitions.carbon_black, price: 25 },

    // Multicam (Price: $25 - $30)
    { id: "multicam", ...definitions.multicam, price: 30 },
    { id: "multicam_black", ...definitions.multicam_black, price: 30 },
    { id: "multicam_arid", ...definitions.multicam_arid, price: 30 },
    { id: "multicam_tropic", ...definitions.multicam_tropic, price: 30 },
];